<template>
  <div class="ship-list-print-table">
    <table v-if="rows.length === 0" class="ship-list-print-native">
      <colgroup>
        <col class="col-id" />
        <col class="col-business" />
        <col class="col-ship-type" />
        <col class="col-terminal" />
        <col class="col-mccs" />
        <col class="col-serial" />
        <col class="col-date" />
        <col class="col-date-narrow" />
        <col class="col-invoice" />
        <col class="col-ship-name" />
        <col class="col-address" />
      </colgroup>
      <thead>
        <tr>
          <th class="col-id">ID</th>
          <th class="col-business">事業区分</th>
          <th class="col-ship-type">出荷種別</th>
          <th class="col-terminal">確定端末</th>
          <th class="col-mccs">MCCS Ver</th>
          <th class="col-serial">シリアル番号</th>
          <th class="col-date">受注日</th>
          <th class="col-date-narrow">着指定日</th>
          <th class="col-invoice">送り状番号</th>
          <th class="col-ship-name">出荷先名</th>
          <th class="col-address">出荷先住所</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td :colspan="columnCount" class="ship-list-print-empty">
            データが存在しません。
          </td>
        </tr>
      </tbody>
    </table>

    <div v-else class="ship-list-print-css-flow">
      <div class="ship-list-print-first-page-cover" aria-hidden="true" />
      <table class="ship-list-print-native ship-list-print-data-table">
        <colgroup>
          <col class="col-id" />
          <col class="col-business" />
          <col class="col-ship-type" />
          <col class="col-terminal" />
          <col class="col-mccs" />
          <col class="col-serial" />
          <col class="col-date" />
          <col class="col-date-narrow" />
          <col class="col-invoice" />
          <col class="col-ship-name" />
          <col class="col-address" />
        </colgroup>
        <thead class="ship-list-print-thead">
          <tr class="ship-list-print-export-date-thead-row">
            <th
              :colspan="columnCount"
              class="ship-list-print-export-date-thead-cell"
            >
              出力日: {{ printExportDate }}
            </th>
          </tr>
          <tr>
            <th class="col-id">ID</th>
            <th class="col-business">事業区分</th>
            <th class="col-ship-type">出荷種別</th>
            <th class="col-terminal">確定端末</th>
            <th class="col-mccs">MCCS Ver</th>
            <th class="col-serial">シリアル番号</th>
            <th class="col-date">受注日</th>
            <th class="col-date-narrow">着指定日</th>
            <th class="col-invoice">送り状番号</th>
            <th class="col-ship-name">出荷先名</th>
            <th class="col-address">出荷先住所</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.id">
            <td class="num col-id">{{ row.id }}</td>
            <td class="col-business">{{ businessLabel(row) }}</td>
            <td class="col-ship-type">{{ shipTypeLabel(row) }}</td>
            <td class="col-terminal">{{ row.orderCompanyName ?? '-' }}</td>
            <td class="col-mccs">{{ mccsLabel(row) }}</td>
            <td class="col-serial">{{ serialLabel(row.deviceSerialCode) }}</td>
            <td class="col-date">{{ dateLabel(row.acceptDate) }}</td>
            <td class="col-date-narrow">{{ dateLabel(row.arrivalDate) }}</td>
            <td class="invoice col-invoice">
              <span class="invoice-supplier-print">
                <span class="invoice-print-icon-cell">
                  <img
                    v-if="row.invoiceSupplier === INVOICE_SUPPLIER.YAMATO"
                    src="@/assets/yamato.png"
                    alt=""
                    class="invoice-print-icon invoice-print-icon--yamato"
                  />
                  <img
                    v-else-if="row.invoiceSupplier === INVOICE_SUPPLIER.SAGAWA"
                    src="@/assets/sagawa.png"
                    alt=""
                    class="invoice-print-icon invoice-print-icon--sagawa"
                  />
                </span>
                <span class="invoice-print-code">{{
                  row.invoiceCode ?? '-'
                }}</span>
              </span>
            </td>
            <td class="text-left col-ship-name">{{ row.shipName }}</td>
            <td class="text-left address col-address">
              {{ addressText(row) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { NumberData } from '@/types/common';
  import { ShipsDataState } from '@/types/ship';
  import { formatDate } from '@/libs/dateFormat';
  import {
    shipType,
    shipBusinessTypeToLabel,
    INVOICE_SUPPLIER
  } from '@/libs/ship';
  import { mccsDeviceType } from '@/libs/device';

  /** A4 landscape printable width (5mm margin per side). */
  const printablePageWidthPx = ((297 - 10) / 25.4) * 96;

  const columnCount = 11;

  const props = defineProps<{
    rows: ShipsDataState[];
    businessTypeLabels?: NumberData | null;
  }>();

  const businessLabel = (row: ShipsDataState) =>
    shipBusinessTypeToLabel(
      { businessType: row.businessType },
      props.businessTypeLabels ?? null
    );

  const shipTypeLabel = (row: ShipsDataState) => {
    let label = shipType[row.type] ?? '-';
    if (row.type === 1) {
      const addInfo = row.orderCompanyId ? '\n(確定)' : ' ';
      label = label + addInfo;
    }
    return label;
  };

  const mccsLabel = (row: ShipsDataState) =>
    mccsDeviceType?.[row.deviceType ?? 0] || '-';

  const serialLabel = (serialList: string[]) => {
    if (!serialList?.length) return '-';
    if (serialList.length === 1) return serialList[0];
    return 'リンク済';
  };

  const dateLabel = (date: string | number | null | undefined) => {
    if (date === null || date === undefined) return '-';
    return formatDate(Number(date));
  };

  const addressText = (row: ShipsDataState) => {
    let zip = row.shipPrefecture + row.shipCity + row.shipAddress;
    if (row.shipAddressSub) zip += row.shipAddressSub;
    return zip;
  };

  const printExportDate = formatDate(Date.now());
  const printPageWidthPx = `${printablePageWidthPx}px`;
</script>

<style scoped lang="scss">
  .ship-list-print-table {
    position: relative;
    width: 100%;
  }

  .ship-list-print-css-flow {
    position: relative;
    width: 100%;
  }

  .ship-list-print-first-page-cover {
    display: block !important;
    position: absolute !important;
    top: -3px !important;
    left: 0 !important;
    right: 0 !important;
    height: 20px !important;
    background: #909399 !important;
    z-index: 1 !important;
    pointer-events: none !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .ship-list-print-export-date-thead-row {
    .ship-list-print-export-date-thead-cell {
      text-align: right;
      font-size: 13px;
      font-weight: 600;
      line-height: 1.2;
      padding: 0 2px 4px;
      border: none !important;
      background: transparent !important;
    }
  }

  .ship-list-print-native {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    /* preview: 14px × (16/12) */
    font-size: 19px;
    line-height: 1.25;

    th,
    td {
      border: 1px solid #909399;
      padding: 3px 4px;
      text-align: center;
      vertical-align: top;
      word-wrap: break-word;
      overflow-wrap: normal;
    }

    /* Narrow columns: slightly tighter horizontal padding */
    .col-id,
    .col-date,
    .col-date-narrow,
    .col-serial,
    .col-mccs,
    .col-business,
    .col-ship-type,
    .col-invoice {
      padding-left: 2px;
      padding-right: 2px;
    }

    /*
     * Width budget (11 cols = 100%):
     * - Content cols: min width for data (nowrap)
     * - 確定端末 / 出荷先名 / 出荷先住所: wider (long text)
     */
    .col-id {
      width: 5%;
    }

    .col-business {
      width: 4%;
    }

    .col-ship-type {
      width: 5%;
    }

    .col-terminal {
      width: 11%;
    }

    .col-mccs {
      width: 6.5%;
    }

    .col-serial {
      width: 15%;
    }

    .col-date,
    .col-date-narrow {
      width: 8%;
    }

    .col-invoice {
      width: 13%;
    }

    .col-ship-name {
      width: 11%;
    }

    .col-address {
      width: 11.5%;
    }

    /* Wrap columns */
    th.col-business,
    td.col-business,
    th.col-ship-type,
    td.col-ship-type,
    th.col-mccs,
    td.col-mccs,
    th.col-terminal,
    td.col-terminal,
    th.col-ship-name,
    td.col-ship-name,
    td.col-address {
      min-width: 0;
      overflow-wrap: normal;
      word-break: normal;
    }

    th.col-ship-type,
    td.col-ship-type {
      white-space: pre-line;
    }

    th.col-business,
    td.col-business,
    th.col-mccs,
    td.col-mccs,
    th.col-terminal,
    td.col-terminal,
    th.col-ship-name,
    td.col-ship-name {
      white-space: normal;
    }

    thead th {
      text-align: center;
      vertical-align: middle;
    }

    td.col-terminal,
    td.col-ship-name,
    td.col-address {
      text-align: left;
    }

    td.col-address {
      overflow-wrap: break-word;
    }

    /* Content columns (single line) — invoice wraps: 設定なし allows up to 255 chars */
    .col-id,
    th.col-serial,
    td.col-serial,
    th.col-date,
    td.col-date,
    th.col-date-narrow,
    td.col-date-narrow,
    th.col-invoice {
      white-space: nowrap;
      word-break: normal;
      line-height: 1.25;
    }

    td.col-invoice {
      min-width: 0;
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-all;
      line-height: 1.25;
    }

    thead {
      display: table-header-group;
    }

    tbody {
      display: table-row-group;
    }

    tbody tr {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    th {
      font-weight: 600;
      background: #f5f7fa;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .text-left {
      text-align: left;
    }

    .address {
      white-space: normal;
    }

    .col-id {
      white-space: nowrap;
    }

    .invoice {
      white-space: normal;
    }

    /* inline-table: Chrome print ignores flex align-items (icon sits on baseline). */
    .invoice-supplier-print {
      display: inline-table;
      border-collapse: collapse;
      border-spacing: 0;
      max-width: 100%;
      vertical-align: middle;
      line-height: 1;
    }

    .invoice-print-icon-cell {
      display: table-cell;
      vertical-align: middle;
      width: 1%;
      padding: 0 2px 0 0;
      line-height: 0;
    }

    .invoice-print-icon {
      display: block;
      width: auto;
      height: 1em;
      object-fit: contain;
    }

    .invoice-print-icon--yamato,
    .invoice-print-icon--sagawa {
      height: 1em;
    }

    .invoice-print-code {
      display: table-cell;
      vertical-align: middle;
      min-width: 0;
      line-height: 1;
      text-align: left;
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-all;
    }

    .ship-list-print-empty {
      padding: 16px;
      text-align: center;
    }
  }
</style>

<style lang="scss">
  /* Ship list: all print / not-print rules live here so ShipList.vue stays screen-only. */

  @media not print {
    .ship-list .ship-list-print-title-stats {
      display: none !important;
    }

    .ship-list .ship-list-print-only {
      position: fixed !important;
      left: -10000px !important;
      top: 0 !important;
      width: v-bind(printPageWidthPx) !important;
      max-width: v-bind(printPageWidthPx) !important;
      visibility: hidden !important;
      pointer-events: none !important;
      z-index: -1 !important;
      overflow: hidden !important;
      height: auto !important;
      box-sizing: border-box !important;

      .ship-list-print-native {
        font-size: 16px !important;
        line-height: 1.2 !important;

        th,
        td {
          padding: 2px 4px !important;
        }
      }
    }
  }

  @page ship-list-landscape {
    size: A4 landscape;
    margin: 5mm;
  }

  @media print {
    html:has(.ship-list),
    body:has(.ship-list),
    #app:has(.ship-list) {
      height: auto !important;
      min-height: 0 !important;
      overflow: visible !important;
    }

    html:has(.ship-list) {
      page: ship-list-landscape;
    }

    .ship-list {
      height: auto !important;
      min-height: 0 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      overflow: visible !important;
      max-width: none !important;

      .base-header,
      .sidebar-container,
      .header-button-area,
      .filter-form-horizontal,
      .filter-display,
      .bulk-approve-btn,
      .sidebar-toggle-btn {
        display: none !important;
      }

      & > div:not(.main) {
        display: none !important;
      }

      .main {
        padding-top: 0 !important;
        display: block !important;
        height: auto !important;
        min-height: 0 !important;
        max-height: none !important;
        overflow: visible !important;
      }

      .main-content {
        width: 100% !important;
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
      }

      .main-container {
        width: 100% !important;
        max-width: none !important;
        margin-top: 0 !important;
        padding-top: 0 !important;
        overflow: visible !important;
        overflow-x: visible !important;
        height: auto !important;
        min-height: 0 !important;
      }

      .header-container {
        width: 100%;
        margin: 0 !important;
        padding: 0 !important;
        height: auto !important;
        min-height: 0 !important;
        flex-wrap: wrap;
        align-items: flex-start;
      }

      .ship-list-print-title-block {
        margin: 0 !important;
        padding: 0 !important;
      }

      .ship-list-print-page-title-row {
        display: flex !important;
        justify-content: space-between !important;
        align-items: flex-start !important;
        width: 100% !important;
        gap: 12px !important;
      }

      .ship-list-print-export-date--header {
        display: block !important;
        flex-shrink: 0 !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        line-height: 1.2 !important;
        white-space: nowrap !important;
      }

      .ship-list-print-data-table thead.ship-list-print-thead {
        display: table-header-group !important;
      }

      .ship-list-print-export-date-thead-row
        .ship-list-print-export-date-thead-cell {
        text-align: right !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        background: transparent !important;
        border: none !important;
      }

      .ship-list-print-first-page-cover {
        display: block !important;
        position: absolute !important;
        top: -3px !important;
        left: 0 !important;
        right: 0 !important;
        height: 20px !important;
        background: #ffffff !important;
        z-index: 1 !important;
        pointer-events: none !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      .page-title {
        font-size: 24px;
        width: 100% !important;
        flex: 0 0 100%;
      }

      .ship-list-print-title-stats {
        display: flex !important;
        flex-wrap: wrap;
        align-items: center;
        gap: 11px 27px;
        width: 100%;
        margin-top: 11px;
        margin-bottom: 0;
        font-size: 15px;
        line-height: 1.4;
        text-align: left;
        font-weight: 500;
      }

      .ship-list-print-title-stats .count-strong {
        font-weight: bold;
      }

      .ship-list-print-title-stat {
        white-space: nowrap;
      }

      .el-table {
        display: none !important;
      }

      .ship-list-print-only {
        display: block !important;
        position: static !important;
        left: auto !important;
        top: auto !important;
        visibility: visible !important;
        width: 100% !important;
        max-width: none !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
        box-sizing: border-box;
        z-index: auto !important;
        break-after: avoid-page;
        page-break-after: avoid;
      }

      .ship-list-print-table,
      .ship-list-print-css-flow {
        width: 100% !important;
        max-width: none !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
        box-sizing: border-box;
      }

      .ship-list-print-native {
        width: 100% !important;
        max-width: none !important;
        table-layout: fixed !important;
        font-size: 16px !important;

        tbody tr {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }

        th,
        td {
          padding: 2px !important;
        }

        .col-id,
        .col-date,
        .col-date-narrow,
        .col-serial,
        .col-mccs,
        .col-business,
        .col-ship-type,
        .col-invoice {
          padding-left: 2px !important;
          padding-right: 2px !important;
        }

        td.col-invoice,
        .invoice,
        .invoice-print-code {
          white-space: normal !important;
          overflow-wrap: anywhere !important;
          word-break: break-all !important;
        }

        .invoice-supplier-print {
          display: inline-table !important;
          border-collapse: collapse !important;
          max-width: 100% !important;
          width: auto !important;
          vertical-align: middle !important;
          line-height: 1 !important;
        }

        .invoice-print-icon-cell {
          display: table-cell !important;
          vertical-align: middle !important;
          line-height: 0 !important;
          padding: 0 2px 0 0 !important;
          border: none !important;
        }

        .invoice-print-icon {
          display: block !important;
          height: 1em !important;
          width: auto !important;
        }

        .invoice-print-code {
          display: table-cell !important;
          vertical-align: middle !important;
          line-height: 1 !important;
        }
      }

      /* ShipList screen uses .ship-list tbody/thead font-size; reset for native print table */
      .ship-list-print-only tbody,
      .ship-list-print-only thead {
        font-size: 16px;
        line-height: 1.2;
      }
    }

    body:has(.ship-list) .el-overlay {
      display: none !important;
    }
  }
</style>
